import React, { useState } from 'react';
import { BaseDropdownFilterMenu } from '@/components/uiKit/Dropdown';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CardDropdownItem {
  id: string;
  label: string;
}

interface CardDropdownProps {
  defaultSelection?: CardDropdownItem;
  allowNoDefaultSelection?: boolean;
  items: CardDropdownItem[];
  onSelect?: (item: any) => void;
  fixedTitle?: React.ReactNode;
}

export const CardDropdown = React.forwardRef(function CardDropdown(
  { items, onSelect, defaultSelection, allowNoDefaultSelection, fixedTitle }: CardDropdownProps,
  ref
) {
  const [selectedItems, setSelectedItems] = useState<CardDropdownItem | undefined>(() => {
    if (defaultSelection) return defaultSelection;
    if (allowNoDefaultSelection) return undefined; // nothing selected initially
    return items[0];
  });
  const [menuOpen, setMenuOpen] = useState(false);

  React.useImperativeHandle(ref, () => ({
    reset: () => setSelectedItems(undefined),
  }));

  const handleSelection = (item: CardDropdownItem) => {
    setSelectedItems(item);
    onSelect && onSelect(item);
  };

  const isSelected = (id: string) => {
    return id === selectedItems?.id;
  };
  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild className="min-w-fit ">
        <Button
          variant="ghost"
          className={cn(
            ' h-fit border-x-0 focus:border-primary-500 dark:focus:border-primary-400 bg-tranparent justify-between m-0 px-2 rounded-lg',
            menuOpen ? 'bg-gray-300 dark:bg-gray-700' : ''
          )}
        >
          {fixedTitle || selectedItems?.label}
          <span className={`${menuOpen && 'rotate-[180deg]'}`}>
            <ChevronDown />
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-full ">
        {items.map((item) => (
          <DropdownMenuItem
            key={item?.id}
            onClick={() => handleSelection(item)}
            className={cn(
              'flex justify-between gap-2 p-2 hover:cursor-pointer',
              isSelected(item?.id) ? 'font-semibold' : ''
            )}
          >
            {item?.label}
            <Check className={cn(isSelected(item?.id) ? 'opacity-100' : 'opacity-0')} />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
